import Api from "@/services/tallyReportsService";
import { getDmsApiErrorMessage } from "@/utils/dmsApiError";
import { readCompanyOrganizationalRoleFromStorage } from "@/utils/permissionsUser";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

/**
 * Stable key for /dms/dashboard/filter-options: dedupe in the UI + reject stale responses.
 * Includes distributor scope so outlet lists refetch when distributor changes.
 * Also includes outletName so distributor list refetches when outlet changes (reverse dependency).
 */
export function buildFilterOptionsRequestKey(arg = {}) {
  const companyId = arg.companyId != null ? String(arg.companyId) : "";
  const state = arg.state != null ? String(arg.state).trim() : "";
  const district = arg.district != null ? String(arg.district).trim() : "";
  const city = arg.city != null ? String(arg.city).trim() : "";
  const distributorName = arg.distributorName != null ? String(arg.distributorName).trim() : "";
  const distributorId = arg.distributorId != null ? String(arg.distributorId).trim() : "";
  const outletName = arg.outletName != null ? String(arg.outletName).trim() : "";
  return JSON.stringify({ companyId, state, district, city, distributorName, distributorId, outletName });
}

// Async thunks for DMS list pages
export const fetchManufacturers = createAsyncThunk(
  "dmsLists/fetchManufacturers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getManufacturers(params);
      return response;
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch manufacturers"));
    }
  }
);

export const fetchManufacturersFilterOptions = createAsyncThunk(
  "dmsLists/fetchManufacturersFilterOptions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getManufacturersFilterOptions(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        getDmsApiErrorMessage(error, "Failed to fetch manufacturers filter options")
      );
    }
  }
);

export const fetchFilterOptions = createAsyncThunk(
  "dmsLists/fetchFilterOptions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getFilterOptions(params);
      return response;
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch filter options"));
    }
  }
);

export const fetchDistributors = createAsyncThunk(
  "dmsLists/fetchDistributors",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getDistributors(params);
      return response;
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch distributors"));
    }
  }
);

export const fetchDistributorsSales = createAsyncThunk(
  "dmsLists/getDistributorsSales",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getDistributorsSales(params);
      return response;
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch distributors sales"));
    }
  }
);

export const fetchOutlets = createAsyncThunk(
  "dmsLists/fetchOutlets",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getOutlets(params);
      return response;
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch outlets"));
    }
  }
);

export const fetchSKUs = createAsyncThunk(
  "dmsLists/fetchSKUs",
  async (params = {}, { rejectWithValue }) => {
    try {
      // Logged-in company role from localStorage companyData
      const companyRole = readCompanyOrganizationalRoleFromStorage();
      // Dual context: (1) distributor + outlet selected, or (2) manufacturer login (show Pr + Sc by default via mapping)
      const isDualContext =
        (params.distributorName && params.outletName) ||
        params.isManufacturer === true ||
        companyRole === "manufacturer";

      if (isDualContext) {
        const response = await Api.tallyReports.getSKUsDualContext(params);
        return response;
      } else {
        const response = await Api.tallyReports.getSKUs(params);
        return response;
      }
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch SKUs"));
    }
  }
);

export const fetchInvoices = createAsyncThunk(
  "dmsLists/fetchInvoices",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getInvoices(params);
      return response;
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch invoices"));
    }
  }
);

export const fetchInvoiceDetail = createAsyncThunk(
  "dmsLists/fetchInvoiceDetail",
  async ({ id, distributorId, distributorName }, { rejectWithValue }) => {
    try {
      const params = {};
      if (distributorId) params.distributorId = distributorId;
      if (distributorName) params.distributorName = distributorName;
      const response = await Api.tallyReports.getInvoiceDetail(
        id,
        Object.keys(params).length ? params : {}
      );
      return response;
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch invoice detail"));
    }
  }
);

export const fetchSecondarySalesInvoices = createAsyncThunk(
  "dmsLists/fetchSecondarySalesInvoices",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getSecondarySalesInvoices(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        getDmsApiErrorMessage(error, "Failed to fetch secondary sales invoices")
      );
    }
  }
);

export const fetchSecondarySalesInvoiceDetail = createAsyncThunk(
  "dmsLists/fetchSecondarySalesInvoiceDetail",
  async ({ id, distributorId, companyId, distributorName }, { rejectWithValue }) => {
    try {
      if (!distributorId) {
        return rejectWithValue("distributorId is required for secondary sales invoice detail");
      }
      const params = { distributorId };
      if (companyId) {
        params.companyId = companyId;
      }
      if (distributorName) {
        params.distributorName = distributorName;
      }
      const response = await Api.tallyReports.getSecondarySalesInvoiceDetail(id, params);
      return response;
    } catch (error) {
      return rejectWithValue(
        getDmsApiErrorMessage(error, "Failed to fetch secondary sales invoice detail")
      );
    }
  }
);

export const fetchMappedPurchaseInvoiceDetail = createAsyncThunk(
  "dmsLists/fetchMappedPurchaseInvoiceDetail",
  async ({ id, distributorId, companyId, distributorName }, { rejectWithValue }) => {
    try {
      if (!distributorId) {
        return rejectWithValue("distributorId is required for mapped purchase invoice detail");
      }
      const params = { distributorId };
      if (companyId) {
        params.companyId = companyId;
      }
      if (distributorName) {
        params.distributorName = distributorName;
      }
      const response = await Api.tallyReports.getMappedPurchaseInvoiceDetail(id, params);
      return response;
    } catch (error) {
      return rejectWithValue(
        getDmsApiErrorMessage(error, "Failed to fetch mapped purchase invoice detail")
      );
    }
  }
);

export const fetchSales = createAsyncThunk(
  "dmsLists/fetchSales",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getSales(params);
      return response;
    } catch (error) {
      return rejectWithValue(getDmsApiErrorMessage(error, "Failed to fetch sales transactions"));
    }
  }
);

export const fetchPurchase = createAsyncThunk(
  "dmsLists/fetchPurchase",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getPurchase(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        getDmsApiErrorMessage(error, "Failed to fetch purchase transactions")
      );
    }
  }
);

export const fetchReceivable = createAsyncThunk(
  "dmsLists/fetchReceivable",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getReceivable(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        getDmsApiErrorMessage(error, "Failed to fetch receivable transactions")
      );
    }
  }
);

export const fetchPayables = createAsyncThunk(
  "dmsLists/fetchPayables",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Api.tallyReports.getPayables(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        getDmsApiErrorMessage(error, "Failed to fetch payables transactions")
      );
    }
  }
);

const initialState = {
  // Data
  manufacturers: null,
  distributors: null,
  outlets: null,
  skus: null,
  /** All-filtered-row totals from API (not current page). See data.summary / grandSummary. */
  skusGrandSummary: null,
  invoices: null,
  invoiceDetail: null,
  secondarySalesInvoices: null,
  secondarySalesInvoiceDetail: null,
  sales: null,
  purchase: null,
  receivable: null,
  payables: null,

  // Filter Options
  manufacturersFilterOptions: null,
  filterOptions: {
    states: [],
    districts: [],
    cities: [],
    pincodes: [],
    manufacturers: [],
    distributors: [],
    outlets: [],
    categories: [],
    subcategories: [],
  },
  // Base (unfiltered) options — never overwritten by cascaded responses
  baseFilterOptions: {
    states: [],
    districts: [],
    cities: [],
    pincodes: [],
  },
  isFilterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsCompanyId: null, // Track which companyId was used to fetch current filterOptions
  /** Last successfully settled filter-options request (success or error) for dedupe in useFilterOptions */
  filterOptionsLoadedKey: null,
  /** Latest in-flight filter-options request key (stale response guard) */
  lastRequestedFilterOptionsKey: null,
  // Request keys to avoid applying out-of-order responses
  lastRequestedDistributorsKey: null,
  lastRequestedOutletsKey: null,
  lastRequestedSKUsKey: null,
  lastRequestedInvoicesKey: null,
  lastRequestedReceivableKey: null,
  lastRequestedPayablesKey: null,
  lastRequestedSalesKey: null,
  lastRequestedPurchaseKey: null,

  // Pagination metadata
  manufacturersPagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  distributorsPagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  outletsPagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  skusPagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  invoicesPagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  secondarySalesInvoicesPagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  salesPagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  purchasePagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  receivablePagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
  payablesPagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },

  // Loading states
  isManufacturersLoading: false,
  isDistributorsLoading: false,
  isOutletsLoading: false,
  isSKUsLoading: false,
  isInvoicesLoading: false,
  isInvoiceDetailLoading: false,
  isSecondarySalesInvoicesLoading: false,
  isSecondarySalesInvoiceDetailLoading: false,
  isSalesLoading: false,
  isPurchaseLoading: false,
  isReceivableLoading: false,
  isPayablesLoading: false,

  // Error states
  manufacturersError: null,
  distributorsError: null,
  outletsError: null,
  skusError: null,
  invoicesError: null,
  invoiceDetailError: null,
  secondarySalesInvoicesError: null,
  secondarySalesInvoiceDetailError: null,
  salesError: null,
  purchaseError: null,
  receivableError: null,
  payablesError: null,

  // Filters (separate for each page to prevent cross-page filter interference)
  filters: {
    companyId: "",
    startDate: null,
    endDate: null,
    state: "",
    district: "",
    city: "",
    distributorName: "",
    distributorId: "",
    manufacturerId: "",
    manufacturerName: "",
    outletName: "",
    outletId: "",
    pincode: "",
    timeRange: "30",
    comparisonPeriod: "",
    page: 1,
    pageSize: 10,
    search: "",
    categoryIds: "",
    subcategoryIds: "",
    labelIds: "",
    sortBy: "",
    sortDirection: "asc",
  },
  // Page-specific filters
  manufacturersFilters: {
    companyId: "",
    startDate: null,
    endDate: null,
    state: "",
    district: "",
    city: "",
    distributorName: "",
    distributorId: "",
    manufacturerId: "",
    pincode: "",
    timeRange: "30",
    comparisonPeriod: "",
    page: 1,
    pageSize: 10,
    search: "",
    sortBy: "",
    sortDirection: "asc",
  },
  distributorsFilters: {
    companyId: "",
    startDate: null,
    endDate: null,
    state: "",
    district: "",
    city: "",
    distributorName: "",
    distributorId: "",
    manufacturerId: "",
    pincode: "",
    timeRange: "30",
    comparisonPeriod: "",
    page: 1,
    pageSize: 10,
    search: "",
    categoryIds: "",
    subcategoryIds: "",
    labelIds: "",
    sortBy: "",
    sortDirection: "asc",
  },
  outletsFilters: {
    companyId: "",
    startDate: null,
    endDate: null,
    state: "",
    district: "",
    city: "",
    distributorName: "",
    distributorId: "",
    manufacturerId: "",
    pincode: "",
    timeRange: "30",
    comparisonPeriod: "",
    page: 1,
    pageSize: 10,
    search: "",
    categoryIds: "",
    subcategoryIds: "",
    labelIds: "",
    sortBy: "",
    sortDirection: "asc",
  },
  skusFilters: {
    companyId: "",
    startDate: null,
    endDate: null,
    state: "",
    district: "",
    city: "",
    distributorName: "",
    distributorId: "",
    manufacturerId: "",
    manufacturerName: "",
    outletName: "",
    pincode: "",
    timeRange: "30",
    comparisonPeriod: "",
    page: 1,
    pageSize: 10,
    search: "",
    categoryIds: "",
    subcategoryIds: "",
    labelIds: "",
    sortBy: "",
    sortDirection: "asc",
  },
  invoicesFilters: {
    companyId: "",
    startDate: null,
    endDate: null,
    state: "",
    district: "",
    city: "",
    distributorName: "",
    distributorId: "",
    manufacturerId: "",
    pincode: "",
    timeRange: "30",
    comparisonPeriod: "",
    page: 1,
    pageSize: 10,
    search: "",
    sortBy: "",
    sortDirection: "asc",
  },
};

// Stable request key so same params (any key order) match for out-of-order response handling
function getStableSalesRequestKey(params) {
  if (!params || typeof params !== "object") return JSON.stringify(params);
  const sorted = Object.keys(params).sort().reduce((acc, k) => {
    acc[k] = params[k];
    return acc;
  }, {});
  return JSON.stringify(sorted);
}

const dmsListsSlice = createSlice({
  name: "dmsLists",
  initialState,
  reducers: {
    clearError: (state) => {
      state.manufacturersError = null;
      state.distributorsError = null;
      state.outletsError = null;
      state.skusError = null;
      state.invoicesError = null;
      state.invoiceDetailError = null;
      state.salesError = null;
      state.purchaseError = null;
      state.receivableError = null;
      state.payablesError = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setManufacturersFilters: (state, action) => {
      state.manufacturersFilters = { ...state.manufacturersFilters, ...action.payload };
    },
    setDistributorsFilters: (state, action) => {
      state.distributorsFilters = { ...state.distributorsFilters, ...action.payload };
    },
    setOutletsFilters: (state, action) => {
      state.outletsFilters = { ...state.outletsFilters, ...action.payload };
    },
    setSKUsFilters: (state, action) => {
      state.skusFilters = { ...state.skusFilters, ...action.payload };
    },
    setInvoicesFilters: (state, action) => {
      state.invoicesFilters = { ...state.invoicesFilters, ...action.payload };
    },
    clearFilters: (state) => {
      const defaultFilters = {
        companyId: "",
        startDate: null,
        endDate: null,
        state: "",
        district: "",
        city: "",
        distributorName: "",
        distributorId: "",
        manufacturerId: "",
        manufacturerName: "",
        outletName: "",
        outletId: "",
        pincode: "",
        timeRange: "30",
        comparisonPeriod: "",
        page: 1,
        pageSize: 10,
        search: "",
        categoryIds: "",
        subcategoryIds: "",
        labelIds: "",
        sortBy: "",
        sortDirection: "asc",
      };
      state.filters = defaultFilters;
      state.manufacturersFilters = defaultFilters;
      state.distributorsFilters = defaultFilters;
      state.outletsFilters = defaultFilters;
      state.skusFilters = defaultFilters;
      state.invoicesFilters = defaultFilters;
    },
    clearData: (state) => {
      state.manufacturers = null;
      state.distributors = null;
      state.outlets = null;
      state.skus = null;
      state.skusGrandSummary = null;
      state.invoices = null;
      state.invoiceDetail = null;
      state.sales = null;
      state.purchase = null;
      state.receivable = null;
      state.payables = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch manufacturers
      .addCase(fetchManufacturers.pending, (state) => {
        state.isManufacturersLoading = true;
        state.manufacturersError = null;
      })
      .addCase(fetchManufacturers.fulfilled, (state, action) => {
        state.isManufacturersLoading = false;
        state.manufacturers = action.payload;
        state.manufacturersError = null;

        // Extract pagination metadata - handle different response structures
        // Check for: payload.pagination, payload.data.pagination, payload.meta, payload.data.meta
        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];



        // Get pagination values from pagination meta, or use filters/defaults
        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;

        // Use paginationMeta.total if available, otherwise use data length as fallback
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;

        // Calculate totalPages - use paginationMeta.totalPages if available
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        // Always update pagination state
        state.manufacturersPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };

      })
      .addCase(fetchManufacturers.rejected, (state, action) => {
        state.isManufacturersLoading = false;
        state.manufacturersError = action.payload;
      })

      // Fetch manufacturers filter options
      .addCase(fetchManufacturersFilterOptions.pending, () => {
        // No loading state needed for filter options
      })
      .addCase(fetchManufacturersFilterOptions.fulfilled, (state, action) => {
        if (action.payload?.success && action.payload?.data) {
          state.manufacturersFilterOptions = action.payload.data;
        }
      })
      .addCase(fetchManufacturersFilterOptions.rejected, (state) => {
        // Set default filter options on error
        state.manufacturersFilterOptions = {
          states: ["All States"],
          districts: ["All Districts"],
          cities: ["All Cities"],
          pincodes: [],
          manufacturers: [
            { id: "", name: "All Manufacturers", value: "", label: "All Manufacturers" },
          ],
        };
      })

      // Fetch generic filter options
      .addCase(fetchFilterOptions.pending, (state, action) => {
        state.isFilterOptionsLoading = true;
        state.filterOptionsError = null;
        state.filterOptionsCompanyId = action.meta.arg?.companyId ?? null;
        state.lastRequestedFilterOptionsKey = buildFilterOptionsRequestKey(action.meta.arg);
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        const reqKey = buildFilterOptionsRequestKey(action.meta.arg);
        if (state.lastRequestedFilterOptionsKey !== reqKey) {
          state.isFilterOptionsLoading = false;
          return;
        }

        state.isFilterOptionsLoading = false;
        state.filterOptionsLoadedKey = reqKey;
        if (action.payload?.success && action.payload?.data) {
          const payload = action.payload.data;
          // Keep base (unfiltered) options when response has no state/district — so clearing a
          // selection always has the full list to fall back to.
          const requestArg = action.meta.arg || {};
          const isBaseRequest = !requestArg.state && !requestArg.district && !requestArg.city;
          const prev = state.filterOptions || {};

          if (isBaseRequest) {
            // Store the full unfiltered lists permanently
            state.baseFilterOptions = {
              states: Array.isArray(payload.states) && payload.states.length > 0 ? payload.states : (state.baseFilterOptions?.states || []),
              districts: Array.isArray(payload.districts) ? payload.districts : (state.baseFilterOptions?.districts || []),
              cities: Array.isArray(payload.cities) ? payload.cities : (state.baseFilterOptions?.cities || []),
              pincodes: Array.isArray(payload.pincodes) ? payload.pincodes : (state.baseFilterOptions?.pincodes || []),
            };
          }

          state.filterOptions = {
            states: Array.isArray(payload.states) && payload.states.length > 0
              ? payload.states
              : (state.baseFilterOptions?.states || prev.states || []),
            // For cascaded fields: use payload result; if nothing returned and this IS a base
            // request it means genuinely empty, otherwise fall back to base full list
            districts: Array.isArray(payload.districts)
              ? (payload.districts.length > 0 ? payload.districts : (isBaseRequest ? [] : (state.baseFilterOptions?.districts || [])))
              : (prev.districts || []),
            cities: Array.isArray(payload.cities)
              ? (payload.cities.length > 0 ? payload.cities : (isBaseRequest ? [] : (state.baseFilterOptions?.cities || [])))
              : (prev.cities || []),
            pincodes: Array.isArray(payload.pincodes)
              ? (payload.pincodes.length > 0 ? payload.pincodes : (isBaseRequest ? [] : (state.baseFilterOptions?.pincodes || [])))
              : (prev.pincodes || []),
            manufacturers: Array.isArray(payload.manufacturers) ? payload.manufacturers : (prev.manufacturers || []),
            distributors: Array.isArray(payload.distributors) ? payload.distributors : (prev.distributors || []),
            outlets: Array.isArray(payload.outlets) ? payload.outlets : (prev.outlets || []),
            categories: Array.isArray(payload.categories) ? payload.categories : (prev.categories || []),
            subcategories: Array.isArray(payload.subcategories) ? payload.subcategories : (prev.subcategories || []),
          };
        }
        state.filterOptionsError = null;
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        const reqKey = buildFilterOptionsRequestKey(action.meta.arg);
        if (state.lastRequestedFilterOptionsKey !== reqKey) {
          state.isFilterOptionsLoading = false;
          return;
        }
        state.isFilterOptionsLoading = false;
        state.filterOptionsLoadedKey = reqKey;
        state.filterOptionsError = action.payload;
        // Keep default values on error
      })

      // Fetch distributors
      .addCase(fetchDistributors.pending, (state, action) => {
        state.isDistributorsLoading = true;
        state.distributorsError = null;
        state.lastRequestedDistributorsKey = JSON.stringify(action.meta.arg || {});
      })
      .addCase(fetchDistributors.fulfilled, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedDistributorsKey !== requestedKey) {
          state.isDistributorsLoading = false;
          return;
        }
        state.isDistributorsLoading = false;
        state.distributors = action.payload;
        state.distributorsError = null;

        // Extract pagination metadata - handle different response structures
        // Check for: payload.pagination, payload.data.pagination, payload.meta, payload.data.meta
        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        // Get pagination values from pagination meta, or use filters/defaults
        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;

        // Use paginationMeta.total if available, otherwise use data length as fallback
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;

        // Calculate totalPages - use paginationMeta.totalPages if available
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        // Always update pagination state
        state.distributorsPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchDistributors.rejected, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedDistributorsKey !== requestedKey) {
          state.isDistributorsLoading = false;
          return;
        }
        state.isDistributorsLoading = false;
        state.distributorsError = action.payload;
      })

      .addCase(fetchDistributorsSales.pending, (state) => {
        state.isDistributorsLoading = true;
        state.distributorsError = null;
      })
      .addCase(fetchDistributorsSales.fulfilled, (state, action) => {
        state.isDistributorsLoading = false;
        state.distributors = action.payload;
        state.distributorsError = null;

        // Extract pagination metadata - handle different response structures
        // Check for: payload.pagination, payload.data.pagination, payload.meta, payload.data.meta
        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        // Get pagination values from pagination meta, or use filters/defaults
        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;

        // Use paginationMeta.total if available, otherwise use data length as fallback
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;

        // Calculate totalPages - use paginationMeta.totalPages if available
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        // Always update pagination state
        state.distributorsPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchDistributorsSales.rejected, (state, action) => {
        state.isDistributorsLoading = false;
        state.distributorsError = action.payload;
      })

      // Fetch outlets
      .addCase(fetchOutlets.pending, (state, action) => {
        state.isOutletsLoading = true;
        state.outletsError = null;
        state.lastRequestedOutletsKey = JSON.stringify(action.meta.arg || {});
      })
      .addCase(fetchOutlets.fulfilled, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedOutletsKey !== requestedKey) {
          state.isOutletsLoading = false;
          return;
        }
        state.isOutletsLoading = false;
        state.outlets = action.payload;
        state.outletsError = null;

        // Extract pagination metadata - handle different response structures
        // Check for: payload.pagination, payload.data.pagination, payload.meta, payload.data.meta
        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        // Extract unique outlets and add to filterOptions
        if (Array.isArray(dataArray) && dataArray.length > 0) {
          const uniqueOutletsMap = new Map();
          dataArray.forEach((outlet) => {
            const outletName = outlet.outletName || outlet.name;
            const outletId = outlet.outletId || outlet.id || outletName;
            if (outletName && !uniqueOutletsMap.has(outletId)) {
              uniqueOutletsMap.set(outletId, {
                id: outletId,
                name: outletName,
                value: outletId,
                label: outletName,
              });
            }
          });
          // Merge with existing outlets (don't overwrite, just add new ones)
          const existingOutlets = state.filterOptions.outlets || [];
          const existingIds = new Set(existingOutlets.map((o) => o.id || o.value));
          const newOutlets = Array.from(uniqueOutletsMap.values()).filter(
            (o) => !existingIds.has(o.id || o.value)
          );
          state.filterOptions.outlets = [...existingOutlets, ...newOutlets];
        }

        // Get pagination values from pagination meta, or use filters/defaults
        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;

        // Use paginationMeta.total if available, otherwise use data length as fallback
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;

        // Calculate totalPages - use paginationMeta.totalPages if available
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        // Always update pagination state
        state.outletsPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchOutlets.rejected, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedOutletsKey !== requestedKey) {
          state.isOutletsLoading = false;
          return;
        }
        state.isOutletsLoading = false;
        state.outletsError = action.payload;
      })

      // Fetch SKUs
      .addCase(fetchSKUs.pending, (state, action) => {
        state.isSKUsLoading = true;
        state.skusError = null;
        state.skusGrandSummary = null;
        state.lastRequestedSKUsKey = JSON.stringify(action.meta.arg || {});
      })
      .addCase(fetchSKUs.fulfilled, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedSKUsKey !== requestedKey) {
          state.isSKUsLoading = false;
          return;
        }
        state.isSKUsLoading = false;
        state.skus = action.payload;
        state.skusError = null;

        // Extract pagination metadata - handle different response structures
        const payload = action.payload;
        state.skusGrandSummary =
          payload?.grandSummary ??
          payload?.data?.summary ??
          null;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        // Get pagination values from pagination meta, or use filters/defaults
        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;

        // Use paginationMeta.total if available, otherwise use data length as fallback
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;

        // Calculate totalPages - use paginationMeta.totalPages if available
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        // Always update pagination state
        state.skusPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchSKUs.rejected, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedSKUsKey !== requestedKey) {
          state.isSKUsLoading = false;
          return;
        }
        state.isSKUsLoading = false;
        state.skusError = action.payload;
      })

      // Fetch invoices
      .addCase(fetchInvoices.pending, (state, action) => {
        state.isInvoicesLoading = true;
        state.invoicesError = null;
        state.lastRequestedInvoicesKey = JSON.stringify(action.meta.arg || {});
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedInvoicesKey !== requestedKey) {
          state.isInvoicesLoading = false;
          return;
        }
        state.isInvoicesLoading = false;
        state.invoices = action.payload;
        state.invoicesError = null;

        // Extract pagination metadata - handle different response structures
        // Check for: payload.pagination, payload.data.pagination, payload.meta, payload.data.meta
        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        // Get pagination values from pagination meta, or use filters/defaults
        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;

        // Use paginationMeta.total if available, otherwise use data length as fallback
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;

        // Calculate totalPages - use paginationMeta.totalPages if available
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        // Always update pagination state
        state.invoicesPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedInvoicesKey !== requestedKey) {
          state.isInvoicesLoading = false;
          return;
        }
        state.isInvoicesLoading = false;
        state.invoicesError = action.payload;
      })

      // Fetch invoice detail
      .addCase(fetchInvoiceDetail.pending, (state) => {
        state.isInvoiceDetailLoading = true;
        state.invoiceDetailError = null;
      })
      .addCase(fetchInvoiceDetail.fulfilled, (state, action) => {
        state.isInvoiceDetailLoading = false;
        state.invoiceDetail = action.payload;
        state.invoiceDetailError = null;
      })
      .addCase(fetchInvoiceDetail.rejected, (state, action) => {
        state.isInvoiceDetailLoading = false;
        state.invoiceDetailError = action.payload;
      })

      // Fetch secondary sales invoices
      .addCase(fetchSecondarySalesInvoices.pending, (state) => {
        state.isSecondarySalesInvoicesLoading = true;
        state.secondarySalesInvoicesError = null;
      })
      .addCase(fetchSecondarySalesInvoices.fulfilled, (state, action) => {
        state.isSecondarySalesInvoicesLoading = false;
        state.secondarySalesInvoices = action.payload;
        state.secondarySalesInvoicesError = null;

        // Extract pagination metadata
        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        state.secondarySalesInvoicesPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchSecondarySalesInvoices.rejected, (state, action) => {
        state.isSecondarySalesInvoicesLoading = false;
        state.secondarySalesInvoicesError = action.payload;
      })

      // Fetch secondary sales invoice detail
      .addCase(fetchSecondarySalesInvoiceDetail.pending, (state) => {
        state.isSecondarySalesInvoiceDetailLoading = true;
        state.secondarySalesInvoiceDetailError = null;
      })
      .addCase(fetchSecondarySalesInvoiceDetail.fulfilled, (state, action) => {
        state.isSecondarySalesInvoiceDetailLoading = false;
        state.secondarySalesInvoiceDetail = action.payload;
        state.secondarySalesInvoiceDetailError = null;
      })
      .addCase(fetchSecondarySalesInvoiceDetail.rejected, (state, action) => {
        state.isSecondarySalesInvoiceDetailLoading = false;
        state.secondarySalesInvoiceDetailError = action.payload;
      })

      // Mapped purchase invoice detail — reuse secondary detail state shape
      .addCase(fetchMappedPurchaseInvoiceDetail.pending, (state) => {
        state.isSecondarySalesInvoiceDetailLoading = true;
        state.secondarySalesInvoiceDetailError = null;
      })
      .addCase(fetchMappedPurchaseInvoiceDetail.fulfilled, (state, action) => {
        state.isSecondarySalesInvoiceDetailLoading = false;
        state.secondarySalesInvoiceDetail = action.payload;
        state.secondarySalesInvoiceDetailError = null;
      })
      .addCase(fetchMappedPurchaseInvoiceDetail.rejected, (state, action) => {
        state.isSecondarySalesInvoiceDetailLoading = false;
        state.secondarySalesInvoiceDetailError = action.payload;
      })

      // Fetch sales
      .addCase(fetchSales.pending, (state, action) => {
        state.isSalesLoading = true;
        state.salesError = null;
        state.lastRequestedSalesKey = getStableSalesRequestKey(action.meta.arg || {});
      })
      .addCase(fetchSales.fulfilled, (state, action) => {
        const requestedKey = getStableSalesRequestKey(action.meta.arg || {});
        if (state.lastRequestedSalesKey !== requestedKey) {
          state.isSalesLoading = false;
          return;
        }
        state.isSalesLoading = false;
        state.sales = action.payload;
        state.salesError = null;

        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        state.salesPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchSales.rejected, (state, action) => {
        const requestedKey = getStableSalesRequestKey(action.meta.arg || {});
        if (state.lastRequestedSalesKey !== requestedKey) {
          state.isSalesLoading = false;
          return;
        }
        state.isSalesLoading = false;
        state.salesError = action.payload;
      })

      // Fetch purchase
      .addCase(fetchPurchase.pending, (state, action) => {
        state.isPurchaseLoading = true;
        state.purchaseError = null;
        state.lastRequestedPurchaseKey = JSON.stringify(action.meta.arg || {});
      })
      .addCase(fetchPurchase.fulfilled, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedPurchaseKey !== requestedKey) {
          state.isPurchaseLoading = false;
          return;
        }
        state.isPurchaseLoading = false;
        state.purchase = action.payload;
        state.purchaseError = null;

        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        state.purchasePagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchPurchase.rejected, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedPurchaseKey !== requestedKey) {
          state.isPurchaseLoading = false;
          return;
        }
        state.isPurchaseLoading = false;
        state.purchaseError = action.payload;
      })

      // Fetch receivable
      .addCase(fetchReceivable.pending, (state, action) => {
        state.isReceivableLoading = true;
        state.receivableError = null;
        state.lastRequestedReceivableKey = JSON.stringify(action.meta.arg || {});
      })
      .addCase(fetchReceivable.fulfilled, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedReceivableKey !== requestedKey) {
          state.isReceivableLoading = false;
          return;
        }
        state.isReceivableLoading = false;
        state.receivable = action.payload;
        state.receivableError = null;

        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        state.receivablePagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchReceivable.rejected, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedReceivableKey !== requestedKey) {
          state.isReceivableLoading = false;
          return;
        }
        state.isReceivableLoading = false;
        state.receivableError = action.payload;
      })

      // Fetch payables
      .addCase(fetchPayables.pending, (state, action) => {
        state.isPayablesLoading = true;
        state.payablesError = null;
        state.lastRequestedPayablesKey = JSON.stringify(action.meta.arg || {});
      })
      .addCase(fetchPayables.fulfilled, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedPayablesKey !== requestedKey) {
          state.isPayablesLoading = false;
          return;
        }
        state.isPayablesLoading = false;
        state.payables = action.payload;
        state.payablesError = null;

        const payload = action.payload;
        const paginationMeta =
          payload?.pagination || payload?.data?.pagination || payload?.meta || payload?.data?.meta;
        const dataArray = payload?.data?.data || payload?.data || [];

        const pageSize = paginationMeta?.pageSize || state.filters.pageSize || 10;
        const currentPage = paginationMeta?.page || state.filters.page || 1;
        const total =
          paginationMeta?.total !== undefined && paginationMeta.total !== null
            ? paginationMeta.total
            : dataArray.length;
        const totalPages =
          paginationMeta?.totalPages !== undefined && paginationMeta.totalPages !== null
            ? paginationMeta.totalPages
            : pageSize > 0 && total > 0
              ? Math.ceil(total / pageSize)
              : total > 0
                ? 1
                : 0;

        state.payablesPagination = {
          total: total || 0,
          page: currentPage,
          pageSize: pageSize,
          totalPages: totalPages > 0 ? totalPages : 1,
        };
      })
      .addCase(fetchPayables.rejected, (state, action) => {
        const requestedKey = JSON.stringify(action.meta.arg || {});
        if (state.lastRequestedPayablesKey !== requestedKey) {
          state.isPayablesLoading = false;
          return;
        }
        state.isPayablesLoading = false;
        state.payablesError = action.payload;
      });
  },
});

export const {
  clearError,
  setFilters,
  setManufacturersFilters,
  setDistributorsFilters,
  setOutletsFilters,
  setSKUsFilters,
  setInvoicesFilters,
  clearFilters,
  clearData,
} = dmsListsSlice.actions;
export default dmsListsSlice.reducer;
